import { useEventCallback } from '@mui/material/utils'
import { sortBy, uniqBy } from 'es-toolkit'
import { memo, useEffect, useRef, useState } from 'react'
import { useFormState } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box } from '../../../../components/Box.tsx'
import {
	Checkbox,
	InputAdornment,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	MenuItem,
	TextField,
	Typography,
} from '../../../../components/generic.tsx'
import { I18n } from '../../../../components/I18n.tsx'
import { Icon } from '../../../../components/Icon.tsx'
import { SearchField } from '../../../../components/SearchField.tsx'
import { Stack } from '../../../../components/Stack.tsx'
import type { AssetLocation } from '../../../../data/locations.tsx'
import { ids } from '../../../../ids.ts'
import { useSearchLocations } from '../../../locations/locationUtils.ts'
import { toggleLocation } from '../locationSelection.ts'
import type { BaseReportForm } from '../schema.ts'
import { useSelectLocations } from '../useSelectLocations.ts'

export const LOCATION_ROW_HEIGHT = 48

export const MultipleLocationPicker = ({
	locations,
	selectedLocations,
	onLocationsSelected,
}: {
	locations: AssetLocation[]
	selectedLocations: AssetLocation[]
	onLocationsSelected: (locations: AssetLocation[]) => void
}) => {
	const { t } = useTranslation()
	const selectLocations = useSelectLocations(onLocationsSelected)
	// The rows are memoized, so they get a callback that never changes
	const onToggle = useEventCallback((location: AssetLocation) => selectLocations(toggleLocation(selectedLocations, location)))
	const [search, setSearch] = useState('')
	const [assetType, setAssetType] = useState('')

	const searchedLocations = useSearchLocations(locations, search)
	const visibleLocations = assetType ? searchedLocations.filter((location) => location.assetType === assetType) : searchedLocations
	const selectedIds = selectedLocations.map(({ id }) => id)
	const { errors } = useFormState<BaseReportForm>({ name: 'assetVrns' })
	const tooManySelected = errors.assetVrns?.type === 'too_big'
	const sortedLocations = sortBy(visibleLocations, ['name'])
	const listRef = useRef<HTMLUListElement>(null)
	const previousSelectedIds = useRef(selectedIds)
	const assetTypeOptions = sortBy(
		uniqBy(locations, (location) => location.assetType).map(({ assetType: type, icon }) => ({
			type,
			icon,
			label: t(($) => $.assets.types[type]),
		})),
		['label'],
	)

	useEffect(() => {
		const addedId = selectedLocations.find(({ id }) => !previousSelectedIds.current.includes(id))?.id
		previousSelectedIds.current = selectedLocations.map(({ id }) => id)
		if (addedId)
			listRef.current?.querySelector(`[data-ta="${CSS.escape(addedId)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
	}, [selectedLocations])

	return (
		<>
			<Stack>
				<SearchField
					data-ta={ids.reports.form.areaOfInterest.search}
					clearDataTa={ids.reports.form.areaOfInterest.searchXBtn}
					autoFocus
					leadingIcon
					value={search}
					onChange={setSearch}
				/>
				<TextField
					data-ta={ids.reports.form.areaOfInterest.assetType}
					select
					fullWidth
					value={assetType}
					onChange={(event) => setAssetType(event.target.value)}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position='start'>
									<Icon name='BarsFilter' />
								</InputAdornment>
							),
						},
						select: {
							displayEmpty: true,
							renderValue: (value) =>
								assetTypeOptions.find(({ type }) => type === value)?.label ?? (
									<Typography color='textSecondary'>
										<I18n i18nKey={($) => $.reports.form.assetTypeFilter} />
									</Typography>
								),
						},
						htmlInput: { 'aria-label': t(($) => $.reports.form.assetTypeFilter) },
					}}
				>
					<MenuItem value='' dense>
						<I18n i18nKey={($) => $.reports.form.allAssetTypes} />
					</MenuItem>
					{assetTypeOptions.map(({ type, icon, label }) => (
						<MenuItem key={type} value={type} dense>
							<Box flex gap={1} align='center' overflow='hidden'>
								<ListItemIcon sx={{ minWidth: 14 }}>
									<Icon name={icon} sx={{ fontSize: 14 }} />
								</ListItemIcon>
								{label}
							</Box>
						</MenuItem>
					))}
				</TextField>
			</Stack>
			<Box
				overflow='auto'
				minHeight={Math.min(4 * LOCATION_ROW_HEIGHT, sortedLocations.length * LOCATION_ROW_HEIGHT)}
				maxHeight={6 * LOCATION_ROW_HEIGHT}
			>
				{sortedLocations.length > 0 ? (
					<List ref={listRef} data-ta={ids.reports.form.areaOfInterest.location}>
						{sortedLocations.map((location) => {
							const checked = selectedIds.includes(location.id)
							return (
								<LocationRow
									key={location.id}
									location={location}
									checked={checked}
									disabled={tooManySelected && !checked}
									onToggle={onToggle}
								/>
							)
						})}
					</List>
				) : (
					<Typography color='textSecondary'>
						<I18n i18nKey={($) => $.reports.form.noMatchingAssets} />
					</Typography>
				)}
			</Box>
		</>
	)
}

const LocationRow = memo(
	({
		location,
		checked,
		disabled,
		onToggle,
	}: {
		location: AssetLocation
		checked: boolean
		disabled: boolean
		onToggle: (location: AssetLocation) => void
	}) => (
		<ListItem
			data-ta={location.id}
			disablePadding
			slotProps={{ secondaryAction: { sx: { pointerEvents: 'none' } } }}
			secondaryAction={<Checkbox size='large' checked={checked} disabled={disabled} tabIndex={-1} aria-hidden />}
		>
			<ListItemButton role='checkbox' aria-checked={checked} selected={checked} disabled={disabled} onClick={() => onToggle(location)}>
				<ListItemIcon>
					<Icon name={location.icon} color='textSecondary' />
				</ListItemIcon>
				<ListItemText primary={<Typography noWrap>{location.name}</Typography>} />
			</ListItemButton>
		</ListItem>
	),
)
